import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError, API_BASE } from "../lib/api";
import { fmtCLP, todayISO } from "../lib/format";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";

const SECTION_LABEL = {
  activo: "Activos",
  pasivo: "Pasivos",
  patrimonio: "Patrimonio",
};

export default function BalanceGeneral() {
  const [hasta, setHasta] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reportes/balance-general", { params: { hasta } });
      setData(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasta]);

  const renderSection = (key) => {
    const items = data.secciones[key] || [];
    return (
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <div className="px-5 py-3 border-b border-border bg-background">
          <div className="label-caps">{SECTION_LABEL[key]}</div>
        </div>
        <table className="acc-table">
          <tbody>
            {items.map((it) => (
              <tr key={it.codigo}>
                <td className="num text-[12px] text-muted-foreground" style={{ width: 100 }}>{it.codigo}</td>
                <td>{it.nombre}</td>
                <td className="num">{fmtCLP(it.saldo)}</td>
              </tr>
            ))}
            {key === "patrimonio" && (data.resultados_acumulados || 0) !== 0 && (
              <tr>
                <td className="num text-[12px] text-muted-foreground">—</td>
                <td className="italic text-muted-foreground">Resultados acumulados (años anteriores)</td>
                <td className="num">{fmtCLP(data.resultados_acumulados)}</td>
              </tr>
            )}
            {key === "patrimonio" && data.utilidad_ejercicio !== 0 && (
              <tr>
                <td className="num text-[12px] text-muted-foreground">—</td>
                <td className="italic text-muted-foreground">Utilidad/Pérdida del ejercicio</td>
                <td className="num">{fmtCLP(data.utilidad_ejercicio)}</td>
              </tr>
            )}
            {items.length === 0 && key !== "patrimonio" && (
              <tr><td colSpan={3} className="text-center text-muted-foreground py-4 text-[12px]">Sin movimientos</td></tr>
            )}
            {items.length === 0 && key === "patrimonio"
              && !(data.utilidad_ejercicio || data.resultados_acumulados) && (
              <tr><td colSpan={3} className="text-center text-muted-foreground py-4 text-[12px]">Sin movimientos</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-background font-semibold">
              <td colSpan={2} className="text-right pr-3 label-caps">Total {SECTION_LABEL[key]}</td>
              <td className="num text-primary">{fmtCLP(data.totales[key])}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  return (
    <Page>
      <PageHeader
        title="Balance general"
        subtitle="Estado de situación financiera"
        testid="balance-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              data-testid="balance-hasta"
              className="px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
            />
            <button
              onClick={() => window.open(`${API_BASE}/reportes/balance-general/excel?hasta=${hasta}`, "_blank")}
              data-testid="balance-excel"
              className="inline-flex items-center gap-1.5 border border-success text-success hover:bg-success/10 text-sm px-3 py-1.5 rounded-sm"
            >
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button
              onClick={() => window.open(`${API_BASE}/reportes/balance-general/pdf?hasta=${hasta}`, "_blank")}
              data-testid="balance-pdf"
              className="inline-flex items-center gap-1.5 border border-destructive text-destructive hover:bg-destructive/10 text-sm px-3 py-1.5 rounded-sm"
            >
              <FileText className="h-4 w-4" /> PDF
            </button>
          </div>
        }
      />

      {loading || !data ? (
        <div className="text-muted-foreground text-sm">Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">{renderSection("activo")}</div>
            <div className="space-y-4">
              {renderSection("pasivo")}
              {renderSection("patrimonio")}
            </div>
          </div>

          <div className="mt-6 bg-card border border-border rounded-md p-5 grid grid-cols-2 gap-6">
            <div>
              <div className="label-caps">Total activos</div>
              <div className="num text-2xl font-semibold text-primary mt-1" data-testid="total-activo">
                {fmtCLP(data.total_activo)}
              </div>
            </div>
            <div>
              <div className="label-caps">Total pasivos + patrimonio</div>
              <div className="num text-2xl font-semibold text-primary mt-1" data-testid="total-pasivo-patrimonio">
                {fmtCLP(data.total_pasivo_patrimonio)}
              </div>
            </div>
            <div className="col-span-2 pt-3 border-t border-border">
              <div className="label-caps">Diferencia</div>
              <div
                className={`num text-lg mt-1 ${
                  Math.abs(data.total_activo - data.total_pasivo_patrimonio) < 1 ? "text-success" : "text-destructive"
                }`}
                data-testid="balance-diferencia"
              >
                {fmtCLP(data.total_activo - data.total_pasivo_patrimonio)}
              </div>
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
